import type { Block } from '@pageboss/shared';
import { InspectorSection } from './inspector-section';
import { InspectorField } from './inspector-field';
import { SegmentedControl } from './segmented-control';
import { Toggle } from './toggle';
import { ImageUploadField } from './image-upload-field';

type HeroBlock = Extract<Block, { type: 'hero' }>;
type HeroProps = HeroBlock['props'];
type Cta = NonNullable<HeroProps['primaryCta']>;

const LAYOUT_OPTIONS = [
  { value: 'centered' as const, label: 'Centered' },
  { value: 'split-left' as const, label: 'Image L' },
  { value: 'split-right' as const, label: 'Image R' },
];

function emptyCta(): Cta {
  return { label: 'Get started', href: '' };
}

interface CtaSectionProps {
  label: string;
  helper: string;
  cta: Cta | undefined;
  onChange: (next: Cta | undefined) => void;
}

function CtaSection({ label, helper, cta, onChange }: CtaSectionProps) {
  const enabled = cta !== undefined;
  return (
    <div className="flex flex-col gap-3">
      <Toggle
        label={label}
        description={helper}
        checked={enabled}
        onChange={(checked) => onChange(checked ? cta ?? emptyCta() : undefined)}
      />
      {enabled && cta ? (
        <div className="rounded-[var(--radius-sm)] bg-surface-sunken p-3 flex flex-col gap-3">
          <InspectorField
            label="Label"
            value={cta.label}
            placeholder="Get a free quote"
            onChange={(v) => onChange({ ...cta, label: v })}
          />
          <InspectorField
            label="Link"
            type="url"
            value={cta.href}
            placeholder="https://... or #contact"
            onChange={(v) => onChange({ ...cta, href: v })}
          />
        </div>
      ) : null}
    </div>
  );
}

interface HeroInspectorProps {
  block: HeroBlock;
  onChange: (next: HeroProps) => void;
}

export function HeroInspector({ block, onChange }: HeroInspectorProps) {
  const props = block.props;
  // patch helper that omits undefined keys so optional schema fields
  // (primaryCta / secondaryCta / image / eyebrow / subhead) can be
  // cleared by passing undefined.
  const patch = (next: Partial<HeroProps>) => {
    const merged: HeroProps = { ...props, ...next };
    if (next.primaryCta === undefined && 'primaryCta' in next) delete merged.primaryCta;
    if (next.secondaryCta === undefined && 'secondaryCta' in next) delete merged.secondaryCta;
    if (next.image === undefined && 'image' in next) delete merged.image;
    onChange(merged);
  };

  return (
    <>
      <InspectorSection>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">Layout</span>
          <SegmentedControl
            ariaLabel="Layout"
            value={props.layout}
            options={LAYOUT_OPTIONS}
            onChange={(v) => patch({ layout: v })}
          />
          <p className="text-xs text-text-tertiary mt-0.5">
            Edit eyebrow, headline, and subhead directly on the canvas.
          </p>
        </div>
      </InspectorSection>

      <InspectorSection label="Buttons">
        <div className="flex flex-col gap-5">
          <CtaSection
            label="Show primary button"
            helper="The main call-to-action — usually 'Get started' or 'Book a quote'"
            cta={props.primaryCta}
            onChange={(next) => patch({ primaryCta: next })}
          />
          <CtaSection
            label="Show secondary button"
            helper="A lighter alternative — usually 'Learn more' or 'See pricing'"
            cta={props.secondaryCta}
            onChange={(next) => patch({ secondaryCta: next })}
          />
        </div>
      </InspectorSection>

      <InspectorSection label="Image">
        <div className="flex flex-col gap-4">
          <Toggle
            label="Show image"
            description="Appears next to the headline in split layouts, or above it when centered"
            checked={props.image !== undefined}
            onChange={(checked) =>
              patch({ image: checked ? props.image ?? { url: '', alt: '' } : undefined })
            }
          />
          {props.image ? (
            <>
              <ImageUploadField
                label="Image"
                url={props.image.url}
                onChange={(url) =>
                  patch({ image: { url, alt: props.image?.alt ?? '' } })
                }
              />
              <InspectorField
                label="Alt text"
                value={props.image.alt}
                placeholder="Tech installing a water heater"
                helperText="Describe the image for screen readers"
                onChange={(alt) =>
                  patch({ image: { url: props.image?.url ?? '', alt } })
                }
              />
            </>
          ) : null}
        </div>
      </InspectorSection>
    </>
  );
}
